import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as API from "../Endpoints/Endpoints";
import { jwtDecode } from 'jwt-decode';

const EditProfile = () => {
  const { regid } = useParams(); // Get userID from URL parameters
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    membername: "",
    email: "", // Ensure email is part of the formData state
    refcode: "",
    organizationname: "",
    dob: "",
    gender: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      const userId = parseInt(decoded.id, 10);
      setUserId(userId);
      localStorage.setItem("id", userId);
      setEmail(decoded.email);  // Set the email from decoded token
      setFormData((prevData) => ({
        ...prevData,
        email: decoded.email,  
      }));
    } else {
      // Handle token absence (perhaps redirect to login)
    }
  }, []);

  const url =API.GET_EMAIL_API(email);

  // Fetch user data based on email
  useEffect(() => {
    const fetchUserDataByEmail = async () => {
      if (email) {
        try {
          const response = await fetch(`${API.GET_EMAIL_API}/email/${email}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });

          if (!response.ok) throw new Error("Failed to fetch user data");

          const userData = await response.json();
          setFormData((prevData) => ({
            ...prevData,
            membername: userData.membername || "",
            email: userData.email || "",  // Ensure the email is populated from fetched data
            refcode: userData.refcode || "",
            organizationname: userData.organizationname || "",
            dob: userData.dob || "",
            gender: userData.gender || "",
          }));
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchUserDataByEmail();
  }, [email]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Make sure regid is passed correctly in the URL
      const response = await fetch(`${API.PUT_REGISTER_API}/edit/${regid}`, {
        method: "PUT", // Use PUT for updates
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          membername: formData.membername,
          refcode: formData.refcode,
          organizationname: formData.organizationname,
          gender: formData.gender,
          dob: formData.dob,
        }),
      });
  
      if (response.ok) {
        alert("Profile updated successfully!");
        navigate("/home"); // Redirect after successful update
      } else {
        const errorData = await response.json();
        alert(`Update failed: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("An error occurred while updating the profile.");
    }
  };    

  return (
    <div className="flex justify-center items-center min-h-screen py-10 px-4 md:px-0 bg-gray-100">
      <div className="flex w-full max-w-4xl bg-white rounded-lg shadow-lg p-8">
        <div className="w-2/3 pr-8">
          <h2 className="text-4xl font-bold mb-6 text-center">Edit Profile</h2>
          {isLoading ? (
            <p className="text-center text-lg">Loading...</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="membername" className="block text-lg font-medium text-gray-700">
                  Member Name
                </label>
                <input
                  type="text"
                  id="membername"
                  name="membername"
                  value={formData.membername}
                  onChange={handleInputChange}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="email" className="block text-lg font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email} // Automatically populate email
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                  readOnly // Make email field read-only
                />
              </div>
              <div className="mb-6">
                <label htmlFor="refcode" className="block text-lg font-medium text-gray-700">
                  Reference Code
                </label>
                <input
                  type="text"
                  id="refcode"
                  name="refcode"
                  value={formData.refcode}
                  onChange={handleInputChange}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="organizationname" className="block text-lg font-medium text-gray-700">
                  Organization Name
                </label>
                <input
                  type="text"
                  id="organizationname"
                  name="organizationname"
                  value={formData.organizationname}
                  onChange={handleInputChange}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="dob" className="block text-lg font-medium text-gray-700">
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="dob"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label htmlFor="gender" className="block text-lg font-medium text-gray-700">
                  Gender
                </label>
                <input
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md text-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <button
                  type="submit"
                  className="w-full py-3 px-6 border border-transparent rounded-md shadow-sm text-white bg-[#e64a32] hover:bg-[#e64a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e64a32] text-lg"
                >
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
